import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Markdown } from '@/components/markdown';
import { CheckCircle, Circle, Download, Printer, ArrowLeft, Link2, AlertTriangle } from 'lucide-react';

type Step = { title: string; description: string; };

interface SOPDetailProps {
  sop: {
    id: string;
    title: string;
    content: string;
    category: string;
    departments: string[];
    applicableRoles: string[];
    tags: string[];
    sopStatus: string;
    version: string;
    reviewDate: string;
    sourceProvenance: string;
    stepsJson: string;
    reviewNotes: string;
    conflictNotes: string;
  };
  relatedSops: { id: string; title: string; category: string }[];
  onBack: () => void;
  onSelectRelated: (id: string) => void;
}

export default function SOPDetailView({ sop, relatedSops, onBack, onSelectRelated }: SOPDetailProps) {
  const steps = useMemo<Step[]>(() => {
    if (!sop.stepsJson) return [];
    try { return JSON.parse(sop.stepsJson); } catch { return []; }
  }, [sop.stepsJson]);

  const [checkedSteps, setCheckedSteps] = useState<Set<number>>(new Set());

  const toggleStep = (idx: number) => {
    setCheckedSteps(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${sop.title}</title>
      <style>body{font-family:-apple-system,sans-serif;max-width:700px;margin:0 auto;padding:40px;color:#1a1a1a;line-height:1.6}
      h1{font-size:22px;margin-bottom:8px}h2{font-size:16px;margin-top:24px}.meta{color:#888;font-size:12px;margin-bottom:24px}
      .step{padding:12px;border:1px solid #ddd;border-radius:6px;margin-bottom:8px}.step-num{font-weight:600;color:#A36B58}
      @media print{@page{margin:20mm}}</style></head><body>
      <h1>${sop.title}</h1>
      <div class="meta">Category: ${sop.category} | Version: ${sop.version} | Status: ${sop.sopStatus}</div>
      ${sop.content ? `<div>${sop.content}</div>` : ''}
      ${steps.length > 0 ? `<h2>Steps</h2>${steps.map((s, i) => `<div class="step"><span class="step-num">Step ${i + 1}:</span> <strong>${s.title}</strong><br/>${s.description}</div>`).join('')}` : ''}
      </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  const handleDownload = () => {
    const text = `${sop.title}\n${'='.repeat(sop.title.length)}\nCategory: ${sop.category}\nVersion: ${sop.version}\nDepartments: ${sop.departments.join(', ')}\n\n${sop.content}\n\n${steps.length > 0 ? 'Steps:\n' + steps.map((s, i) => `${i + 1}. ${s.title}: ${s.description}`).join('\n') : ''}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sop.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusColor = sop.sopStatus === 'Published' ? 'bg-green-500/10 text-green-700' :
    sop.sopStatus === 'Draft' ? 'bg-yellow-500/10 text-yellow-700' : 'bg-muted text-muted-foreground';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="mt-0.5 shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{sop.title}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <Badge variant="outline" className={statusColor}>{sop.sopStatus}</Badge>
              {sop.category && <Badge variant="secondary">{sop.category}</Badge>}
              <span className="text-xs text-muted-foreground">v{sop.version}</span>
              {sop.reviewDate && <span className="text-xs text-muted-foreground">Review: {sop.reviewDate}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5 h-8 text-xs">
            <Printer className="w-3.5 h-3.5" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5 h-8 text-xs">
            <Download className="w-3.5 h-3.5" /> Download
          </Button>
        </div>
      </div>

      {/* Provenance & Conflicts */}
      {sop.sourceProvenance && (
        <div className="bg-muted/50 rounded-lg px-4 py-2.5 text-xs text-muted-foreground">
          <span className="font-medium">Source:</span> {sop.sourceProvenance}
        </div>
      )}
      {sop.conflictNotes && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-2.5 text-xs text-destructive flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div><span className="font-medium">Conflict:</span> {sop.conflictNotes}</div>
        </div>
      )}

      {/* Departments & Roles */}
      {(sop.departments.length > 0 || sop.applicableRoles.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {sop.departments.map(d => <Badge key={d} variant="outline" className="text-xs">{d}</Badge>)}
          {sop.applicableRoles.map(r => <Badge key={r} variant="outline" className="text-xs bg-primary/5">{r}</Badge>)}
        </div>
      )}

      {/* Content body */}
      {sop.content && (
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="prose prose-sm max-w-none text-foreground">
            <Markdown>{sop.content}</Markdown>
          </div>
        </div>
      )}

      {/* Interactive Steps Checklist */}
      {steps.length > 0 && (
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">Procedure Steps</h3>
            <span className="text-xs text-muted-foreground">{checkedSteps.size}/{steps.length} completed</span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5 mb-4">
            <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${steps.length > 0 ? (checkedSteps.size / steps.length) * 100 : 0}%` }} />
          </div>
          <div className="space-y-2">
            {steps.map((step, idx) => (
              <button
                key={idx}
                onClick={() => toggleStep(idx)}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  checkedSteps.has(idx)
                    ? 'bg-primary/5 border-primary/20'
                    : 'bg-card border-border hover:bg-muted/50'
                }`}
              >
                {checkedSteps.has(idx) ? (
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${checkedSteps.has(idx) ? 'text-primary line-through' : 'text-foreground'}`}>
                    {idx + 1}. {step.title}
                  </p>
                  {step.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Related SOPs */}
      {relatedSops.length > 0 && (
        <div className="border border-border rounded-lg bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Link2 className="w-3.5 h-3.5 text-primary" /> Related SOPs
          </h3>
          <div className="space-y-1.5">
            {relatedSops.map(r => (
              <button
                key={r.id}
                onClick={() => onSelectRelated(r.id)}
                className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors text-sm text-foreground"
              >
                <span className="flex-1">{r.title}</span>
                <Badge variant="outline" className="text-xs">{r.category}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
